/** Compact booking panel for the Bookings grid master-detail row.
 *
 * Unlike BookingSummary (the full detail-page Booking tab), this is a single,
 * dense group of just the most important properties — laid out as a tight
 * multi-column key/value grid so ops can scan a booking inline without
 * navigating away. A link jumps to the full detail page. The values carry the
 * same quick actions as the full page (bookingLinks).
 */
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Alert, Box, Chip, CircularProgress, Link, Stack, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import * as bookingsApi from '@/api/bookings';
import { TagBadgeSet } from '@/components/TagBadgeSet/TagBadgeSet';
import { tagChipActions } from '@/components/TagBadgeSet/tagChipActions';
import { ValueActions, type ValueAction } from '@/components/ValueActions/ValueActions';

import { BookingStateChip } from '../BookingStateChip';
import {
  altCustomIdActions,
  bookingIdActions,
  customIdActions,
  departureActions,
  departureKeyActions,
  emailActions,
  guestActions,
  integrationActions,
  orderActions,
  paxActions,
  phoneActions,
  refActions,
  stateActions,
  tenantActions,
  timestampActions,
  tourActions,
  type BookingCtx,
} from './bookingLinks';
import { CopyableLink, CopyableValue, Ts, userName } from './common';

function Item({
  label,
  actions,
  children,
}: {
  label: string;
  actions?: ValueAction[];
  children: ReactNode;
}) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.3 }}>
        {label}
      </Typography>
      <Typography variant="body2" component="div" sx={{ wordBreak: 'break-word' }}>
        {actions?.length ? <ValueActions actions={actions}>{children}</ValueActions> : children}
      </Typography>
    </Box>
  );
}

export function BookingPanelSummary({ bookingId }: { bookingId: number }) {
  const { data: b, isLoading, error } = useQuery({
    queryKey: bookingsApi.bookingDetailKey(bookingId),
    queryFn: () => bookingsApi.retrieve(bookingId),
    enabled: Number.isFinite(bookingId),
  });

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }
  if (error || !b) return <Alert severity="error">Couldn’t load booking #{bookingId}.</Alert>;

  const guest = b.guest;
  const when = b.start_ts ?? b.date;
  const ctx: BookingCtx = { tenantId: b.tenant?.id };

  return (
    <Box
      sx={{
        p: 1.5,
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        bgcolor: 'background.paper',
      }}
    >
      {/* Header */}
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          {guest ? (
            <ValueActions actions={guestActions(guest, ctx)}>
              <Link component={RouterLink} to={`/users/${guest.id}`} underline="hover">
                {userName(guest)}
              </Link>
            </ValueActions>
          ) : (
            'Booking'
          )}
        </Typography>
        <ValueActions chip actions={stateActions(b.booking_state_display, ctx)}>
          <BookingStateChip booking_state={b.booking_state} booking_state_display={b.booking_state_display} />
        </ValueActions>
        <ValueActions chip actions={bookingIdActions(b.id, ctx)}>
          <Chip size="small" variant="outlined" label={`#${b.id}`} />
        </ValueActions>
        {b.tenant && (
          <ValueActions actions={tenantActions(b.tenant)}>
            <Typography variant="caption" color="text.secondary">
              {b.tenant.name}
            </Typography>
          </ValueActions>
        )}
        <Box sx={{ flexGrow: 1 }} />
        <Link
          component={RouterLink}
          to={`/bookings/${b.id}`}
          underline="hover"
          variant="body2"
          sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}
        >
          Open full page <OpenInNewIcon sx={{ fontSize: 15 }} />
        </Link>
      </Stack>

      {/* Dense key/value grid of the most important properties */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(3, 1fr)', md: 'repeat(4, 1fr)' },
          columnGap: 2,
          rowGap: 1.25,
        }}
      >
        <Item label="Tour" {...(b.tour && { actions: tourActions(b.tour, ctx) })}>
          {b.tour ? (
            <Link component={RouterLink} to={`/tours/${b.tour.id}`} underline="hover">
              {b.tour.name ?? `#${b.tour.id}`}
            </Link>
          ) : (
            '—'
          )}
        </Item>
        <Item label="Departure" {...(when && { actions: departureActions(b, ctx) })}>
          <Ts value={when} />
          {b.departure?.name ? ` · ${b.departure.name}` : ''}
        </Item>
        <Item label="Pax" {...(b.people_count != null && { actions: paxActions(b.people_count, ctx) })}>
          {b.people_count ?? '—'}
        </Item>
        <Item label="Email" {...(guest?.email && { actions: emailActions(guest.email, ctx) })}>
          {guest?.email ? <CopyableLink href={`mailto:${guest.email}`} text={guest.email} label="Email" /> : '—'}
        </Item>
        <Item label="Phone" {...(guest?.phone && { actions: phoneActions(guest.phone, ctx) })}>
          {guest?.phone ? <CopyableLink href={`tel:${guest.phone}`} text={guest.phone} label="Phone" /> : '—'}
        </Item>
        <Item label="Order #" {...((b.order_id || b.order_ref) && { actions: orderActions(b, ctx) })}>
          {b.order_id ? (
            <Link component={RouterLink} to={`/orders/${b.order_id}`} underline="hover">
              {b.order_ref ?? `#${b.order_id}`}
            </Link>
          ) : (
            b.order_ref ?? '—'
          )}
        </Item>
        <Item label="Custom ID" {...(b.custom_id && { actions: customIdActions(b.custom_id, ctx) })}>
          <CopyableValue value={b.custom_id} label="Custom ID" />
        </Item>
        {b.alt_custom_id && (
          <Item label="Alt ID" actions={altCustomIdActions(b.alt_custom_id, ctx)}>
            <CopyableValue value={b.alt_custom_id} label="Alt ID" />
          </Item>
        )}
        <Item
          label="Departure code"
          {...(b.departure_key && { actions: departureKeyActions(b.departure_key, ctx) })}
        >
          <CopyableValue value={b.departure_key || null} label="Departure code" />
        </Item>
        <Item label="Supplier #" {...(b.supplier_ref && { actions: refActions('supplier', b.supplier_ref, ctx) })}>
          <CopyableValue value={b.supplier_ref} label="Supplier #" />
        </Item>
        <Item label="Reseller #" {...(b.reseller_ref && { actions: refActions('reseller', b.reseller_ref, ctx) })}>
          <CopyableValue value={b.reseller_ref} label="Reseller #" />
        </Item>
        <Item label="Integration" {...(b.integration && { actions: integrationActions(b.integration, ctx) })}>
          {b.integration ?? '—'}
        </Item>
        <Item
          label="Created"
          {...(b.created_ts && {
            actions: timestampActions('created', b.created_ts, b.create_source_display, ctx),
          })}
        >
          <Ts value={b.created_ts} relative />
        </Item>
        <Item
          label="Updated"
          {...(b.updated_ts && {
            actions: timestampActions('updated', b.updated_ts, b.update_source_display, ctx),
          })}
        >
          <Ts value={b.updated_ts} relative />
        </Item>
      </Box>

      {/* Tags */}
      <Box sx={{ mt: 1.25 }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25 }}>
          Tags
        </Typography>
        <TagBadgeSet
          tags={b.tags}
          maxVisible={12}
          chipActions={(t) => tagChipActions(t, { tenantId: ctx.tenantId, owner: 'booking' })}
        />
      </Box>
    </Box>
  );
}
